import React, { useRef, useState } from 'react';
import {
  Badge,
  Button,
  Datagrid,
  Icon,
  IconButton,
  Menu,
  Popover,
  Spacer,
  Stack,
  Title
} from '@efast_public/fjd-component-library';
import { useTranslation } from 'react-i18next';
import { UserAndRoleManagementInviteModalSSO } from '../../../../modals/UserAndRoleManagementInviteModalSSO';
import {
  IUserWithRightsDtoV2,
  PagedResponse
} from '../../../../api/models/Einstellungen/userRoleDto';
import {
  isUserAdministratorSSO,
  isUserRegisteredSSO
} from './utils/usersAndRoles';
import { useQuery } from '@tanstack/react-query';
import { BehApiRoutes } from '../../../../api/beh-api';
import UsersAndRolesManagementEditModalSSO from './usersAndRolesManagement/UsersAndRolesManagementEditModalSSO';
import { useNotification } from '../../../../shared/notification/useNotification';
import { useRemoveUser } from '../../../../api/hooks/useRemoveUser';

function UserRowMenuSSO({
  onEdit,
  onDelete,
  id
}: {
  onEdit: () => void;
  onDelete: () => void;
  id: string;
}) {
  const menuRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  return (
    <>
      <IconButton
        ref={menuRef}
        icon="overflow-menu-vertical"
        variant="primary-text"
        label="menu"
        id={`button-${id}`}
        onClick={() => setOpen((o) => !o)}
      />
      <Popover
        anchorElement={menuRef}
        open={open}
        onClose={() => setOpen(false)}
        placement="bottom-right"
      >
        <Menu>
          <Menu.MenuItem
            icon="edit"
            label={t('usersAndRoles.editUserButton')}
            onClick={() => {
              setOpen(false);
              onEdit();
            }}
          />
          <Menu.MenuItem
            icon="trashcan"
            label={t('usersAndRoles.deleteUserButton')}
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
          />
        </Menu>
      </Popover>
    </>
  );
}

export default function UsersAndRoles() {
  const { t } = useTranslation();

  const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] =
    useState<IUserWithRightsDtoV2 | null>(null);
  const { confirmByUser } = useNotification();
  const { mutateAsync: deleteUser } = useRemoveUser();
  const { data: userRolesData, isLoading: isValidating } = useQuery<
    PagedResponse<IUserWithRightsDtoV2>
  >({
    queryKey: [BehApiRoutes.userWithAuthorizations__(0, 10000)]
  });

  let userGroups = userRolesData?.content as IUserWithRightsDtoV2[];

  const isLastAdmin = (users: IUserWithRightsDtoV2[]) => {
    const adminUsers = users.filter(isUserAdministratorSSO);
    return adminUsers.length === 1;
  };

  return (
    <Stack spacing="l">
      <div style={{ width: '50rem' }}>
        <Stack spacing="l">
          <Title level={2} visualLevel={4}>
            {t('usersAndRoles.headline')}
          </Title>
          <div>{t('usersAndRoles.descriptionText')}</div>
          <Button onClick={() => setIsInviteModalVisible(true)}>
            {t('usersAndRoles.addUserButton')}
          </Button>
        </Stack>
      </div>
      <Spacer />
      <Datagrid<IUserWithRightsDtoV2>
        columns={[
          {
            id: 'lastName',
            header: 'Nachname',
            accessorKey: 'lastName',
            enableHiding: true,
            size: 150,
            minSize: 2,
            enableSorting: true
          },
          {
            id: 'firstName',
            header: 'Vorname',
            accessorKey: 'firstName',
            enableHiding: true,
            size: 150,
            minSize: 4,
            enableSorting: true,
            enableResizing: true
          },
          {
            id: 'email',
            header: 'E-Mail Adresse',
            accessorKey: 'email',
            enableHiding: true,
            size: 300,
            minSize: 4,
            enableSorting: true,
            enableResizing: true
          },
          {
            id: 'roles',
            header: 'Rollen',
            accessorKey: 'systemRoles',
            enableHiding: true,
            size: 600,
            minSize: 4,
            enableSorting: false,
            enableResizing: true,
            cell: (row) => {
              const { systemRoles, orgUnitRoles } = row.row.original;
              return (
                <div>
                  {systemRoles.map((role) => (
                    <div key={role}>{role}</div>
                  ))}
                  {orgUnitRoles.map((orgUnit) =>
                    orgUnit.roles.map((role) => (
                      <div key={`${orgUnit.orgUnitName}-${role}`}>
                        {orgUnit.orgUnitName}: {role}
                      </div>
                    ))
                  )}
                </div>
              );
            }
          },
          {
            id: 'isAdmin',
            header: 'Admin',
            accessorKey: 'isAdmin',
            enableHiding: true,
            size: 150,
            minSize: 4,
            enableSorting: false,
            enableResizing: true,
            cell: (row) =>
              isUserAdministratorSSO(row.row.original) && (
                <Icon variant="success" name="checkmark-filled" />
              )
          },
          {
            id: 'status',
            header: 'Status',
            accessorKey: 'status',
            enableHiding: true,
            size: 10,
            minSize: 4,
            enableSorting: false,
            enableResizing: true,
            cell: (row) =>
              isUserRegisteredSSO(row.row.original) ? (
                <Badge
                  size="sm"
                  iconBefore="checkmark-filled"
                  variant="success"
                >
                  registriert
                </Badge>
              ) : (
                <Badge size="sm" variant="inProgress">
                  eingeladen
                </Badge>
              )
          },
          {
            id: 'menu',
            header: '',
            accessorKey: 'menu',
            enableHiding: true,
            size: 4,
            minSize: 2,
            enableSorting: false,
            enableResizing: false,
            // TODO: I think we should use build a menu button including a menu in the complib v2
            cell: (row) => (
              <UserRowMenuSSO
                id={row.row.original.id}
                onEdit={() => setSelectedUserForEdit(row.row.original)}
                onDelete={() =>
                  confirmByUser({
                    callback: () => {
                      deleteUser(row.row.original.id).catch(() => {});
                    },
                    title:
                      'Wollen Sie diese:n Benutzer:in wirklich unwiderruflich löschen?',
                    content:
                      'Diese:r Benutzer:in hat danach keine Zugriffsrechte mehr.'
                  })
                }
              />
            )
          }
        ]}
        data={userGroups ?? []}
        labels={{
          currentPage: 'Aktuelle Seite',
          detailButton: 'Weitere Infos ein-/ausblenden',
          emptyData: 'Keine Einträge',
          nextPage: 'Nächste Seite',
          of: 'Seite {0} von {1}',
          page: 'Seite',
          pageSize: '{0} Einträge pro Seite',
          previousPage: 'Vorherige Seite',
          rows: 'Zeilen',
          tableTitle: '',
          // tableTitle: t('usersAndRoles.tableHeadline'),
          ascBtn: 'Aufsteigend sortieren',
          descBtn: 'Absteigend sortieren',
          hideBtn: 'Spalte ausblenden'
        }}
        manualPagination={false}
        isDataLoading={isValidating}
        dataKey="email"
        manualSorting={false}
        areColumnsHideable
        areColumnsResizeable
        tableHeight={540}
        clickableRows={false}
      />
      {isInviteModalVisible && (
        <UserAndRoleManagementInviteModalSSO
          onClose={() => setIsInviteModalVisible(false)}
        />
      )}
      {selectedUserForEdit && userGroups && (
        <UsersAndRolesManagementEditModalSSO
          user={selectedUserForEdit}
          isLastAdmin={isLastAdmin(userGroups)}
          onClose={() => setSelectedUserForEdit(null)}
        />
      )}
    </Stack>
  );
}
